import { FormArray, FormControl, Validators } from "@angular/forms";

export enum TableFieldType {
  BOOLEAN = 0,
  RADIO = 1,
  // MULTI = 2,
  TEXT = 3,
  INTEGER = 4,
  DECIMAL = 5,
  DROPDOWN = 7,
  DATETIME = 8,
  // DURATION = 9,
  EXPRESSION = 10,
  //PICTURE = 11,
  //TIMESTAMP = 12,
  DATE = 13,
  CONDITION = 14,
  DATERANGE = 15,
  // DATETIME= 16
}

export enum TableFieldConditionOperator {
  AND = 0,
  OR = 1,
}

export enum TableFieldConditionArgumentOperator {
  EQ = 0, // ==
  NEQ = 1, // !=
  GT = 2, // >
  GTE = 3, // >=
  LW = 4, // <
  LWE = 5, // <=
}

export class TableFieldConditionArgument {
  value: number;
  operator: TableFieldConditionArgumentOperator;

  constructor(val: number, oper: TableFieldConditionArgumentOperator) {
    this.value = val;
    this.operator = oper;
  }

  toMap(arg: TableFieldConditionArgument) {
    const map = new Map<string, unknown>();
    map.set("value", arg.value);
    map.set("operator", +arg.operator);
    return map;
  }
}

export class TableFieldCondition {
  expression: string;
  operator: TableFieldConditionOperator;
  oneArgument: boolean;
  argument1: TableFieldConditionArgument;
  argument2?: TableFieldConditionArgument;
  resultTrue: string;
  resultFalse: string;

  constructor(
    exp: string,
    oper: TableFieldConditionOperator,
    resTrue: string,
    resFalse: string,
    arg1: TableFieldConditionArgument,
    oneArg: boolean,
    arg2?: TableFieldConditionArgument
  ) {
    this.expression = exp;
    this.operator = oper;
    this.resultTrue = resTrue;
    this.resultFalse = resFalse;
    this.argument1 = new TableFieldConditionArgument(arg1.value, arg1.operator);
    this.oneArgument = oneArg;
    if (oneArg) {
      this.argument2 = new TableFieldConditionArgument(
        arg2.value,
        arg2.operator
      );
    }
  }
  toMap(condition: TableFieldCondition): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("expression", condition.expression);
    map.set("operator", condition.operator);
    map.set("resultTrue", condition.resultTrue);
    map.set("resultFalse", condition.resultFalse);
    const arg1Map = condition.argument1.toMap(condition.argument1);
    map.set("argument1", arg1Map);
    if (condition.argument1 != null) {
      const arg2Map = condition.argument2.toMap(condition.argument2);
      map.set("argument2", arg2Map);
    }
    return map;
  }
}

export enum FormTableAggregateType {
  SUM = 0,
  CNT = 1,
  AVG = 2,
  MAX = 3,
  MIN = 4,
  CND = 5,
}

export const TableAggregateType: Record<FormTableAggregateType, string> = {
  [FormTableAggregateType.SUM]: "SUM",
  [FormTableAggregateType.AVG]: "AVG",
  [FormTableAggregateType.MAX]: "MAX",
  [FormTableAggregateType.MIN]: "MIN",
  [FormTableAggregateType.CNT]: "CNT",
  [FormTableAggregateType.CND]: "CND",
};

export class TableItem {
  name: string;
  type: TableFieldType;
  columnKey: string;
  sort: number;
  required: boolean;
  visible: boolean;
  width: number;
  values: string[];
  defaultValue: string;
  aggregate?: boolean;
  formTableAggregateType?: FormTableAggregateType | null;
  valueMap?: string;


  constructor(
    name: string,
    type: TableFieldType,
    colKey: string,
    sort?: number,
    width?: number,
    values?: string[],
    required?: boolean,
    visible?: boolean,
    defaultValue?: string,
    aggregate?: boolean,
    aggregateType?: FormTableAggregateType,
    valueMap?: string
  ) {
    this.name = name;
    this.type = type;
    this.columnKey = colKey;
    this.sort = sort ?? 0;
    this.width = width ?? 1;
    this.values = values && [...values];
    this.required = required ?? false;
    this.visible = visible ?? true;
    this.defaultValue = defaultValue ?? null;
    this.aggregate = aggregate ?? false;
    this.formTableAggregateType = +aggregateType || null;
    this.valueMap = valueMap;
  }

  toMap(tableItem: TableItem): Map<string, unknown> {
    const map = new Map<string, unknown>();
    map.set("name", tableItem.name);
    map.set("type", tableItem.type);
    map.set("columnKey", tableItem.columnKey);
    map.set("sort", tableItem.sort ?? 0);
    map.set("width", tableItem.width ?? 1);
    map.set("required", tableItem.required ?? false);
    map.set("visible", tableItem.visible ?? true);
    map.set("defaultValue", tableItem.defaultValue);
    map.set("aggregate", tableItem.aggregate);
    map.set("formTableAggregateType", tableItem.formTableAggregateType);
    map.set("valueMap", tableItem.valueMap);
    if (tableItem.values?.length > 0) {
      const values = [];
      tableItem.values.forEach((v) => {
        values.push(v);
      });
      map.set("values", values);
    }
    return map;
  }
}

export class TableItemForm {
  name = new FormControl();
  type = new FormControl();
  columnKey = new FormControl();
  sort = new FormControl();
  required = new FormControl();
  visible = new FormControl();
  defaultValue = new FormControl();
  width = new FormControl();
  aggregate = new FormControl(false);
  formTableAggregateType = new FormControl();
  values = new FormArray([]);
  valueMap = new FormControl<string | null>(null);

  constructor(item: TableItem) {
    this.name.setValue(item.name);
    this.type.setValue(item.type);
    this.sort.setValue(item.sort);
    this.columnKey.setValue(item.columnKey);
    this.required.setValue(item.required);
    this.visible.setValue(item.visible);
    this.width.setValue(item.width);
    this.defaultValue.setValue(item.defaultValue);
    this.aggregate.setValue(item.aggregate);
    if (item.formTableAggregateType != null) {
      this.formTableAggregateType.setValue(item.formTableAggregateType);
    }
    if (item.valueMap != null) {
      this.valueMap.setValue(item.valueMap);
    }
    if (
      item.values != null &&
      item.values.length > 0 &&
      item.values[0] !== ""
    ) {
      item.values.forEach((i) => {
        this.values.push(new FormControl(i));
      });
      this.values.setValidators(Validators.required);
    }
    this.name.setValidators([Validators.required]);
    this.name.updateValueAndValidity();
    this.type.setValidators([Validators.required]);
    this.type.updateValueAndValidity();
  }
}
